import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { Fab, List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { adminCourseEditorRoutes, adminRoutes, userRoutes } from 'routes'
import { LogoutIcon } from 'assets/icons/logout'
import useUserRole from 'hooks/useUserRole'
import { COGNITO_LOGOUT_URI } from 'config'
import { useAuth } from 'react-oidc-context'

interface Props {
  open: boolean
}

export default function AppDrawerItems({ open }: Props): JSX.Element {
  const intl = useIntl()
  const { isAdmin, isAdminCourseEditor } = useUserRole()
  const { user, signoutRedirect } = useAuth()
  const userInfo = { givenName: user?.profile.given_name ?? '', email: user?.profile.email ?? '' }

  const getAdminRoute = () => {
    if (isAdmin) {
      return adminRoutes
    }
    return adminCourseEditorRoutes
  }

  const items =
    isAdmin || isAdminCourseEditor
      ? getAdminRoute().filter((route) => route.showInMenu)
      : userRoutes.filter((route) => route.showInMenu)

  //each email is most likely a GGB name.surname.
  const getUserInitials = () => {
    const fullName = userInfo?.email || ''
    return fullName
      .split('.')
      .slice(0, 2)
      .map((n: string) => n[0])
  }

  //check the logout it needs to redirect to the login page.
  const handleLogout = async () => {
    await signoutRedirect({
      //post_logout_redirect_uri is added by default to the payload,
      //cognito doesn't accept that, so we have to use logout uri in the extra querry params and
      //we need to set it to undefined.
      id_token_hint: user?.id_token || '',
      post_logout_redirect_uri: undefined,
      extraQueryParams: {
        client_id: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
        logout_uri: COGNITO_LOGOUT_URI || '',
      },
    })
  }

  return (
    <Container>
      <NavList>
        {items.map(({ titleId, path, menuIcon: MenuIcon, disabled = false }, index) => (
          <NavLink
            to={path}
            key={path}
            style={{ pointerEvents: disabled ? 'none' : 'initial', opacity: disabled ? 0.5 : 1 }}
            onClick={(e) => e.stopPropagation()} // Prevents drawer from closing when clicking on a link
          >
            <ListItem button key={index}>
              <ListMenuIcon open={open}>
                <MenuIcon />
              </ListMenuIcon>
              {open && (
                <>
                  <ListItemText primary={intl.formatMessage({ id: titleId })} />
                </>
              )}
            </ListItem>
          </NavLink>
        ))}
      </NavList>
      <ActionContainer onClick={(e) => e.stopPropagation()}>
        <AccountImageContainer key={'user'}>
          <AvatarIcon open={open}>
            <AccountImage
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              color="primary"
            >
              {getUserInitials()}
            </AccountImage>
          </AvatarIcon>
          {open && <ListItemText primary={userInfo?.givenName} />}
        </AccountImageContainer>
        <ListItem button key={'logout'} onClick={handleLogout}>
          <ListMenuIcon open={open}>
            <LogoutIcon />
          </ListMenuIcon>
          {open && (
            <>
              <ListItemText primary={intl.formatMessage({ id: 'strings.logout' })} />
            </>
          )}
        </ListItem>
      </ActionContainer>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`

const NavList = styled(List)`
  padding: 18px 16px;

  & .MuiListItem-gutters {
    padding: 12px 0;
    height: 48px;
  }

  & a {
    display: block;
    text-decoration: none;
    color: ${(props) => props.theme.customPalette.navText};
    font-size: 14px;
    margin-bottom: 4px;
    border-radius: 8px;

    & .MuiListItemIcon-root:first-of-type > svg {
      fill: ${(props) => props.theme.customPalette.iconGray};
      stroke: ${(props) => props.theme.customPalette.iconGray};
    }

    & .MuiListItemIcon-root:last-of-type {
      color: inherit;
    }

    &.active {
      background-color: ${(props) => props.theme.customPalette.navActiveBg};

      & .MuiListItemIcon-root:first-of-type > svg {
        fill: ${(props) => props.theme.palette.primary.main};
        stroke: ${(props) => props.theme.palette.primary.main};
      }

      & .MuiListItemIcon-root:last-of-type {
        color: inherit;
      }
    }
  }
`

const ListMenuIcon = styled(ListItemIcon)<Props>`
  width: 24px;
  min-width: 24px;
  height: 100%;
  margin: 0 12px;
`

const AvatarIcon = styled(ListItemIcon)<Props>`
  width: 24px;
  min-width: 24px;
  height: 100%;
  margin: ${(props) => (props.open ? '0 12px' : 'auto')};
`

const AccountImageContainer = styled(ListItem)`
  pointer-events: none;
`

const AccountImage = styled(Fab)`
  background-color: ${(props) => props.theme.customPalette.light};
  color: ${(props) => props.theme.palette.primary.main};
  height: 24px;
  width: 24px;
  font-weight: 600;
  font-style: italic;
  font-size: 10px;

  &:hover {
    background-color: ${(props) => props.theme.customPalette.light};
    opacity: 0.75;
  }
`

const ActionContainer = styled.div`
  margin-bottom: 72px;
`
